import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Upload,
  Cpu,
  Play,
  BarChart3,
  CalendarClock,
  ChevronRight,
  X,
  CheckCircle,
} from "lucide-react";

const STEPS = [
  {
    icon: Upload,
    title: "Connect Your Data",
    description: "Upload a CSV, connect Google Sheets, or import from your CRM. Veylo AI accepts any structured dataset.",
  },
  {
    icon: Cpu,
    title: "AI Processing",
    description: "Our AI automatically detects column types, missing values, duplicates, and outliers — no configuration needed.",
  },
  {
    icon: Play,
    title: "Run Your First Analysis",
    description: "Head to the AI Workflows page and click 'Run AI Analysis' to see instant schema detection and cleaning recommendations.",
  },
  {
    icon: BarChart3,
    title: "View Insights & Dashboards",
    description: "The AI generates trends, anomalies, correlations, and predictions explained in plain language on your dashboards.",
  },
  {
    icon: CalendarClock,
    title: "Schedule Automated Reports",
    description: "Set up daily, weekly, or monthly analysis runs so your team always has fresh insights without lifting a finger.",
  },
];

type OnboardingWalkthroughProps = {
  /** Called when the user finishes or skips. Persistence is owned by useOnboarding. */
  onComplete: (skipped: boolean) => void;
};

export const OnboardingWalkthrough = ({ onComplete }: OnboardingWalkthroughProps) => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);

  const progress = ((step + 1) / STEPS.length) * 100;
  const currentStep = STEPS[step];
  const Icon = currentStep.icon;

  const finish = (skipped: boolean) => {
    setVisible(false);
    onComplete(skipped);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else finish(false);
  };

  const handleSkip = () => finish(true);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-lg mx-4 shadow-2xl">
        <CardContent className="pt-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">
              Step {step + 1} of {STEPS.length}
            </p>
            <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
              <X className="h-4 w-4 mr-1" /> Skip Tutorial
            </Button>
          </div>

          <Progress value={progress} className="h-1.5" />

          {/* Step content */}
          <div className="text-center space-y-4 py-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Icon className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-bold">{currentStep.title}</h2>
            <p className="text-muted-foreground leading-relaxed max-w-sm mx-auto">
              {currentStep.description}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>
              Back
            </Button>
            <div className="flex gap-1.5">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    i <= step ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
            <Button onClick={handleNext}>
              {step === STEPS.length - 1 ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-1" /> Finish
                </>
              ) : (
                <>
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
